import React, {useState} from 'react'
import PropTypes from 'prop-types'
import { get, sumBy } from 'lodash'

import { Grid } from '@material-ui/core'
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";

import {
  aggregateExAnteYearlyCostValue,
  getBreakDown,
  getExAnteExplanations,
  getExAnteSimulationYearData,
  getExAnteSpecificYearData
} from "../../../../utils";
import {TRANSACTION_TYPE, TRANSACTION_TYPE_VALUES} from "../../../../constants";
import ExAnteAverageLine from "./components/ExAnteLine/ExAnteAverageLine";
import ExAnteSection from "./components/ExAnteSection";
import ExAnteLine from "./components/ExAnteLine/ExAnteLine";
import {toGermanFormat} from "../../../../../../utils/numberFormater";
import {getSubSystemConfigItem} from "../../../../../../utils/utils";
import SafeHtml from "../../../../../../components/SafeHtml";
import DashboardChartSection
  from "../../../../../CustomerDashboardV2/components/Widgets/components/DashboardChartSection";

import useStyles from './styles'

const setTotalFeeFromBreakDowns = (feeData) => {
  feeData.cost_value = sumBy(feeData.breakdown, (fee) => +fee.cost_value || 0)
  feeData.cost_percentage = sumBy(feeData.breakdown, (fee) => +fee.cost_percentage || 0)
}

const dieKostenReduzierenDieRenditeDesInvestmentsWieFolgtTooltip = (
  <>
    In dem Berechnungsmodell wird sich an einigen Depotbanken orientiert, indem im gesamten Zeitraum von einer 0%
    Rendite ausgegangen wird, da über den Verlauf keine sicheren Annahmen getroffen werden können. Daher ist der Bestand
    in der Berechnung kontinuierlich sinkend (nur bei Einmalanlage).
    <br/>
    <br/>
    Beim Sparplan steigt der Durchschnittsbestand aufgrund der regelmäßigen Einzahlungen. Hier wird auch von einer 0%
    Rendite ausgegangen.
  </>
);

function PortfolioExAnteV2(props) {

  const {
    totalValue,
    exAnteData,
    transactionType,
    oneYearSavingsPlan,
    oneYearSavingsPlanTitle,
    isPrivateInvestment,
    isIndividualAssetManagement,
    simulationYears=5,
    defaultExpanded=false,
  } = props;

  const classes = useStyles();
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!exAnteData) {
    return null
  }

  const onlySell = React.useMemo(() => {
    return transactionType === 'sell';
  }, [transactionType]);

  const exAnteJsonStr = JSON.stringify(exAnteData);

  const yearData = React.useMemo(() => {
    return getExAnteSpecificYearData(exAnteData, simulationYears, 1)
  }, [exAnteJsonStr]);

  const twoYearData = React.useMemo(() => {
    return getExAnteSpecificYearData(exAnteData, simulationYears, 2)
  }, [exAnteJsonStr]);

  const threeYearData = React.useMemo(() => {
    return getExAnteSpecificYearData(exAnteData, simulationYears, 3)
  }, [exAnteJsonStr]);

  const fourYearData = React.useMemo(() => {
    return getExAnteSpecificYearData(exAnteData, simulationYears, 4)
  }, [exAnteJsonStr]);

  const fiverYearData = React.useMemo(() => {
    return getExAnteSpecificYearData(exAnteData, simulationYears, 5)
  }, [exAnteJsonStr]);

  const fiveYearValue = React.useMemo(() => {
    return aggregateExAnteYearlyCostValue(exAnteData, simulationYears)
  }, [exAnteJsonStr]);

  const fiveYearSimulationExAnte = React.useMemo(() => {
    return getExAnteSimulationYearData(exAnteData, simulationYears)
  }, [exAnteJsonStr]);


  const getPercentage = (value, total_amount) => {
    return value / total_amount
  };

  const fiveYearPercentage = React.useMemo(() => {
    return getPercentage(fiveYearValue, parseFloat(get(fiveYearSimulationExAnte, 'invested_amount', 0)))
  }, [fiveYearValue, fiveYearSimulationExAnte]);

  const averageCostPercentage = React.useMemo(() => {
    return getPercentage(fiveYearValue / simulationYears, get(exAnteData, `${simulationYears}.ongoing.cost_base_value`, 0))
  }, [fiveYearValue, fiveYearSimulationExAnte]);

  const provisionData = React.useMemo(() => {
    const entry_cost = getBreakDown(get(fiveYearSimulationExAnte, 'initial.service', {}), 'entry_cost');
    return getBreakDown(entry_cost, 'provision');
  }, [fiveYearSimulationExAnte]);

  const followUpProvisionData = React.useMemo(() => {
    const ongoing_cost = getBreakDown(get(fiveYearSimulationExAnte, 'ongoing.product', {}), 'instrument_ongoing_product_fee');
    return getBreakDown(ongoing_cost, 'followup_provision');
  }, [fiveYearSimulationExAnte]);

  const isDisplayProvisionKickbackData = React.useMemo(() => {
    const ongoing_product_fee = getBreakDown(get(fiveYearSimulationExAnte, 'ongoing.product', {}), 'instrument_ongoing_product_fee');
    return getBreakDown(ongoing_product_fee, 'followup_provision_kickback');
  }, [fiveYearSimulationExAnte]);

  const followUpProvisionKickbackData = React.useMemo(() => {
    const ongoing_cost = getBreakDown(get(fiveYearSimulationExAnte, 'ongoing.product', {}), 'instrument_ongoing_product_fee');
    return getBreakDown(ongoing_cost, 'followup_provision_kickback');
  }, [fiveYearSimulationExAnte]);

  const ongoingProductDataBreakdowns = React.useMemo(() => {
    if(isIndividualAssetManagement) {
      const ongoing_cost = getBreakDown(get(fiveYearSimulationExAnte, 'ongoing.product', {}), 'instrument_ongoing_product_fee');

      return {
        activeFonds: getBreakDown(ongoing_cost, 'active_fonds'),
        etf: getBreakDown(ongoing_cost, 'etf'),
        einzeltitel: getBreakDown(ongoing_cost, 'einzeltitel')
      }
    }
  }, [isIndividualAssetManagement, fiveYearSimulationExAnte])

  const individualAssetManagementTooltips = React.useMemo(() => {
    if(isIndividualAssetManagement){
      const tooltips = {};

      Object.entries(getSubSystemConfigItem("onboarding", "individual_asset_management_ex_ante_tooltips") || {}).forEach(([field, msg]) => {
        tooltips[field] = <SafeHtml html={msg} />
      })

      return tooltips;
    }
  }, [isIndividualAssetManagement])

  const ongoingServiceData = React.useMemo(() => {

    if(isIndividualAssetManagement){
      const serviceFee = get(fiveYearSimulationExAnte, 'ongoing.service', {});

      const serviceData = {
        title: "Dienstleistungskosten der Depotstelle",
        tooltip: individualAssetManagementTooltips.ongoingServiceDepot,
        breakdown: [
          {
            title: 'davon Depotführungskosten'
          },
          {
            title: 'davon Kosten f. Kauf und Verkauf über KVG',
            ...getBreakDown(serviceFee, 'kvg_fee')
          },
          {
            title: 'davon Kosten f. Kauf und Verkauf über Börse',
            ...getBreakDown(serviceFee, 'exchange_fee')
          }
        ]
      }

      setTotalFeeFromBreakDowns(serviceData)

      return serviceData
    }

    if(!isPrivateInvestment) {
      const service_fee = getBreakDown(get(fiveYearSimulationExAnte, 'ongoing.service', {}), 'Servicegebühr')
      const brokerServiceFee = getBreakDown(service_fee, 'Vermittler');
      return {
        ...brokerServiceFee,
        title: 'davon an den Vermittler ausgekehrtes laufendes Serviceentgelt',
      }
    }

    const investment_strategy = getBreakDown(get(fiveYearSimulationExAnte, 'ongoing.service', {}), 'investment_strategy')

    return {
      ...investment_strategy,
      title: 'Laufende Gesamtvergütung p.a.',
      breakdown: [{
        ...getBreakDown(investment_strategy, 'investment_strategy_bfv'),
        title: 'hiervon entfallen auf den Vermögensverwalter'
      }, {
        ...getBreakDown(investment_strategy, 'investment_strategy_asset_manager'),
        title: 'hiervon entfallen auf den Anlageberater'
      }, {
        ...getBreakDown(investment_strategy, 'investment_strategy_broker'),
        title: 'hiervon entfallen auf den Vertrieb'
      }]
    }

  }, [isPrivateInvestment, isIndividualAssetManagement, fiveYearSimulationExAnte]);

  const ongoingServiceForIndividualManager = React.useMemo(() => {
    if(isIndividualAssetManagement) {
      const serviceFee = get(fiveYearSimulationExAnte, 'ongoing.service', {});
      const managerFeeData = getBreakDown(serviceFee, 'Dienstleistungskosten des Vermögensverwalters (inkl. MwSt)')

      setTotalFeeFromBreakDowns(managerFeeData)

      return managerFeeData;
    }
  }, [isIndividualAssetManagement, fiveYearSimulationExAnte])

  const explanations = React.useMemo(getExAnteExplanations, []);

  const isBuy = transactionType === TRANSACTION_TYPE_VALUES.BUY;

  return (
    <DashboardChartSection
      title={
        <Grid container spacing={2} alignItems={"center"}>
          <Grid item className={classes.transactionType}><b>{TRANSACTION_TYPE[transactionType]}</b></Grid>
          <Grid item style={{marginLeft: 'auto'}}>
            <Grid container spacing={1}>
              {(fiveYearValue || parseFloat(fiveYearValue) === 0) && (
                <Grid item>
                  <BlueChip label={<>Ex-Ante Kosten <b>{toGermanFormat(fiveYearValue)} €</b></>} />
                </Grid>
              )}
              {totalValue !== undefined && (
                <Grid item>
                  <BlueChip label={<>{isBuy ? 'Anlagebetrag (Einmalanlage)' : 'Veräußerungsbetrag'} <b>{ toGermanFormat(totalValue) } €</b></>} />
                </Grid>
              )}


              {!!oneYearSavingsPlan && (
                <Grid item>
                  <BlueChip label={<>{oneYearSavingsPlanTitle} (jährlich) <b>{ toGermanFormat(oneYearSavingsPlan) } €</b></>} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      }
      expanded={expanded}
      onExpanded={setExpanded}
      content={
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12} className={classes.tableMainHeadline}>
                <Grid container>
                  <Grid item xs={6}>Kostenpunkt</Grid>
                  <Grid item xs={3} style={{textAlign: 'right'}}>in %</Grid>
                  <Grid item xs={3} style={{textAlign: 'right'}}>in €</Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <ExAnteSection
                  title={
                    <TextWithTooltip title={!isIndividualAssetManagement && explanations.ERWERBSKOSTEN}>
                      I. Erwerbskosten / Einstiegsentgelt (einmalig)
                    </TextWithTooltip>
                  }
                  euroValue={get(fiveYearSimulationExAnte, 'initial.cost_value', 0)}
                  percentValue={get(fiveYearSimulationExAnte, 'initial.cost_percentage', 0)}
                >
                  {!isIndividualAssetManagement && (
                    <ExAnteLine
                      title={
                        <TextWithTooltip title={explanations.ERWERBSKOSTEN_PRODUKTKOSTEN}>
                          Summe Produktkosten
                        </TextWithTooltip>
                      }
                      bold
                      withBorder
                      euroValue={get(fiveYearSimulationExAnte, 'initial.product.cost_value', 0)}
                      percentValue={get(fiveYearSimulationExAnte, 'initial.product.cost_percentage', 0)}
                    />
                  )}
                  <ExAnteLine
                    title={
                      <TextWithTooltip title={isIndividualAssetManagement
                        ? individualAssetManagementTooltips.entryService
                        : explanations.ERWERBSKOSTEN_DIENSTLEISTUNGSKOSTEN}>
                        Summe Dienstleistungskosten
                      </TextWithTooltip>
                    }
                    bold
                    numbersTooltip={"Die jeweiligen Ausgabeaufschläge (s. Factsheet) vom Anlagebetrag, Produktkosten und Transaktionsgebühren für Kauf/Verkauf/Sparplan werden hier zusammengerechnet. Hinweis: Ausgabeaufschlag kann rabattiert werden."}
                    euroValue={get(fiveYearSimulationExAnte, 'initial.service.cost_value', 0)}
                    percentValue={get(fiveYearSimulationExAnte, 'initial.service.cost_percentage', 0)}
                  >
                    <ExAnteLine
                      title="davon an den Vermittler ausgekehrt"
                      subTitle={true}
                      numbersTooltip={"Hier wird die Provisionsauskehr für Sie ermittelt. Die Provision wird nach der Nettomethode berechnet  z.B. (100-(100*100/(100+ Ausgabeaufschlag)))."}
                      euroValue={get(provisionData, 'cost_value', 0)}
                      percentValue={get(provisionData, 'cost_percentage', 0)} />
                  </ExAnteLine>
                </ExAnteSection>
                <ExAnteSection
                  title={
                    <TextWithTooltip title={isIndividualAssetManagement
                      ? individualAssetManagementTooltips.ongoingTotal
                      : explanations.LAUFENDE_KOSTEN}>
                      II. Laufende Kosten (p.a.)
                    </TextWithTooltip>
                  }
                  euroValue={get(exAnteData, `${simulationYears}.ongoing.cost_value`, 0)}
                  percentValue={get(exAnteData, `${simulationYears}.ongoing.cost_percentage`, 0)}
                  >
                  <ExAnteLine
                    title={
                      <TextWithTooltip title={isIndividualAssetManagement
                        ? individualAssetManagementTooltips.ongoingProduct
                        : explanations.LAUFENDE_KOSTEN_PRODUKTKOSTEN}>
                        Summe Produktkosten
                      </TextWithTooltip>
                    }
                    bold
                    withBorder
                    numbersTooltip={"Hier werden Verwaltungsgebühren und laufende Vertriebsprovisionen zusammengefasst."}
                    euroValue={get(fiveYearSimulationExAnte, 'ongoing.product.cost_value', 0)}
                    percentValue={get(fiveYearSimulationExAnte, 'ongoing.product.cost_percentage', 0)}
                  >
                    {isIndividualAssetManagement ? (
                      <>
                        <ExAnteLine
                          title={"davon aktive Fonds"}
                          subTitle
                          euroValue={get(ongoingProductDataBreakdowns.activeFonds, 'cost_value', 0)}
                          percentValue={get(ongoingProductDataBreakdowns.activeFonds, 'cost_percentage', 0)}
                        />
                        <ExAnteLine
                          title={
                            <TextWithTooltip title={individualAssetManagementTooltips.ongoingProvision}>
                              darin enthalten: laufende Provision (geschätzt)
                            </TextWithTooltip>
                          }
                          subTitle={true}
                          style={{marginLeft: 40}}
                          euroValue={get(followUpProvisionData, 'cost_value', 0)}
                          percentValue={get(followUpProvisionData, 'cost_percentage', 0)}/>
                        <ExAnteLine
                          title="davon an den Anleger zurückerstatteter"
                          subTitle={true}
                          style={{marginLeft: 40}}
                          euroValue={get(followUpProvisionKickbackData, 'cost_value', 0)}
                          percentValue={get(followUpProvisionKickbackData, 'cost_percentage', 0)}/>
                        <ExAnteLine
                          title={"davon ETFs"}
                          subTitle
                          euroValue={get(ongoingProductDataBreakdowns.etf, 'cost_value', 0)}
                          percentValue={get(ongoingProductDataBreakdowns.etf, 'cost_percentage', 0)}
                        />
                        <ExAnteLine
                          title={"davon Einzeltitel"}
                          subTitle
                          euroValue={get(ongoingProductDataBreakdowns.einzeltitel, 'cost_value', 0)}
                          percentValue={get(ongoingProductDataBreakdowns.einzeltitel, 'cost_percentage', 0)}
                        />
                      </>
                    ) : (<>
                      <ExAnteLine
                        title={
                          <TextWithTooltip title={explanations.LAUFENDE_KOSTEN_PRODUKTKOSTEN_DAVON_LAUFENDE_PROVISION}>
                            davon laufende Provision
                          </TextWithTooltip>
                        }
                        subTitle={true}
                        euroValue={get(followUpProvisionData, 'cost_value', 0)}
                        percentValue={get(followUpProvisionData, 'cost_percentage', 0)}/>
                      {!!isDisplayProvisionKickbackData && (
                        <ExAnteLine
                          title="davon an den Anleger zurückerstattet"
                          subTitle={true}
                          euroValue={get(followUpProvisionKickbackData, 'cost_value', 0)}
                          percentValue={get(followUpProvisionKickbackData, 'cost_percentage', 0)}/>
                      )}
                    </>)}
                  </ExAnteLine>
                  <ExAnteLine
                    title={
                      <TextWithTooltip title={!isIndividualAssetManagement && explanations.LAUFENDE_KOSTEN_DIENSTLEISTUNGSKOSTEN}>
                        Summe Dienstleistungskosten
                      </TextWithTooltip>
                    }
                    bold
                    numbersTooltip={"Hier werden Depotgebühren (bei Neudepots), Transaktionskosten und Bearbeitungsgebühren zusammengerechnet."}
                    euroValue={get(fiveYearSimulationExAnte, 'ongoing.service.cost_value', 0)}
                    percentValue={get(fiveYearSimulationExAnte, 'ongoing.service.cost_percentage', 0)}>
                      {!!ongoingServiceData && (
                        <>
                          <ExAnteLine
                            title={
                              <TextWithTooltip title={ongoingServiceData.tooltip}>
                                {ongoingServiceData.title}
                              </TextWithTooltip>
                            }
                            subTitle={true}
                            euroValue={get(ongoingServiceData, 'cost_value', 0)}
                            percentValue={get(ongoingServiceData, 'cost_percentage', 0)}
                          />
                          {ongoingServiceData.breakdown && ongoingServiceData.breakdown.map((item) => (
                            <ExAnteLine
                              key={item.title}
                              title={item.title}
                              subTitle={true}
                              style={{marginLeft: 40}}
                              euroValue={get(item, 'cost_value', 0)}
                              percentValue={get(item, 'cost_percentage', 0)}
                            />
                          ))}
                        </>
                      )}
                      {!!ongoingServiceForIndividualManager && (
                        <>
                          <ExAnteLine
                            title={
                              <TextWithTooltip title={individualAssetManagementTooltips.ongoingServiceManagers}>
                                {ongoingServiceForIndividualManager.name}
                              </TextWithTooltip>
                            }
                            subTitle
                            euroValue={get(ongoingServiceForIndividualManager, 'cost_value', 0)}
                            percentValue={get(ongoingServiceForIndividualManager, 'cost_percentage', 0)}
                          />
                          {ongoingServiceForIndividualManager.breakdown.map((item) => (
                            <ExAnteLine
                              key={item.name}
                              title={item.name}
                              subTitle
                              style={{marginLeft: 40}}
                              euroValue={get(item, 'cost_value', 0)}
                              percentValue={get(item, 'cost_percentage', 0)}
                            />
                          ))}
                        </>
                      )}
                  </ExAnteLine>
                </ExAnteSection>
                {!isIndividualAssetManagement && (
                  <ExAnteSection
                    title={(
                      <TextWithTooltip title={explanations.VERAUSSERUNGSKOSTEN}>
                       III. Veräußerungskosten (einmalig)
                      </TextWithTooltip>
                    )}
                    euroValue={get(fiveYearSimulationExAnte, 'exit.cost_value', 0)}
                    percentValue={get(fiveYearSimulationExAnte, 'exit.cost_percentage', 0)}
                  >
                    <ExAnteLine
                      title={
                        <TextWithTooltip title={explanations.VERAUSSERUNGSKOSTEN_PRODUKTKOSTEN}>
                          Summe Produktkosten
                        </TextWithTooltip>
                      }
                      bold
                      withBorder
                      euroValue={get(fiveYearSimulationExAnte, 'exit.product.cost_value', 0)}
                      percentValue={get(fiveYearSimulationExAnte, 'exit.product.cost_percentage', 0)} />
                    <ExAnteLine
                      title="Summe Dienstleistungskosten"
                      bold
                      withBorder
                      numbersTooltip={"Hier werden die Transaktionskosten berechnet, welche bei Produktverkäufen anfallen. Die Summe variiert von Depot-Art/ Depotbank."}
                      euroValue={get(fiveYearSimulationExAnte, 'exit.service.cost_value', 0)}
                      percentValue={get(fiveYearSimulationExAnte, 'exit.service.cost_percentage', 0)} />
                  </ExAnteSection>
                )}
              </Grid>
            </Grid>
          </Grid>
          {!onlySell && (
            <>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} className={classes.transactionType}>
                      <TextWithTooltip title={explanations.GESAMTKOSTEN_PROGNOSE}>
                        <b>Gesamtkosten Prognose</b>
                      </TextWithTooltip>
                    </Grid>
                  <Grid item xs={12}>
                    <Grid container>
                      <Grid item xs={12} className={classes.tableMainHeadline}>
                        <Grid container>
                          <Grid item xs={6}>Kostenpunkt</Grid>
                          <Grid item xs={3} style={{textAlign: 'right'}}>in %</Grid>
                          <Grid item xs={3} style={{textAlign: 'right'}}>in €</Grid>
                        </Grid>
                      </Grid>
                      <ExAnteLine
                        title="Gesamtkosten der Anlage bei einer Haltedauer von 5 Jahren"
                        numbersTooltip={"Die Summe der Gesamtprognose errechnet sich aus der Summe der unten aufgelisteten jährlichen Kosten. Dies wird im Verhältnis zur Anlagesumme x wiedergegeben."}
                        euroValue={fiveYearValue}
                        percentValue={fiveYearPercentage}
                        withBorder
                      />
                      <ExAnteLine
                        title={"Durchschnittliche Kosten p.a."}
                        numbersTooltip={"Die durchschnittlichen Kosten p.a. berechnen sich an dem Gesamtdurchschnitt. Allgemein: Wenn es sich um jährliche Kosten handelt, werden diese immer im Verhältnis zum Gesamtdurchschnitt errechnet."}
                        euroValue={fiveYearValue / simulationYears}
                        percentValue={averageCostPercentage}
                        withBorder
                      />
                      {!!isDisplayProvisionKickbackData && (
                        <ExAnteLine
                          title="Rückerstattung der laufenden Provisionen an den Kunden (abhängig vom Volumen)"
                          euroValue={get(followUpProvisionKickbackData, 'cost_value', 0)}
                          percentValue={get(followUpProvisionKickbackData, 'cost_percentage', 0)}
                          withBorder
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} className={classes.transactionType}>
                    <TextWithTooltip title={explanations.DIE_KOSTEN_REDUZIEREN_DIE_RENDITE_DES_INVESTMENTS_WIE_FOLGT}>
                       <b>Die Kosten reduzieren die Rendite des Investments wie folgt</b>
                    </TextWithTooltip>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container>
                      <Grid item xs={12} className={classes.tableMainHeadline}>
                        <Grid container>
                          <Grid item xs={3} />
                          <Grid item xs={3} style={{textAlign: 'right'}}>in %</Grid>
                          <Grid item xs={3} style={{textAlign: 'right'}}>in €</Grid>
                          <Grid item xs={3} style={{textAlign: 'right'}}>Durchschnittsbestand</Grid>
                        </Grid>
                      </Grid>
                      <ExAnteAverageLine
                        title="Im ersten Jahr"
                        numbersTooltip={dieKostenReduzierenDieRenditeDesInvestmentsWieFolgtTooltip}
                        euroValue={get(yearData, 'cost.cost_value', 0)}
                        averageValue={get(yearData, 'cost.cost_base_value', 0)}
                        percentValue={get(yearData, 'cost.cost_percentage', 0)} />
                      <ExAnteAverageLine
                        title="Im zweiten Jahr"
                        numbersTooltip={dieKostenReduzierenDieRenditeDesInvestmentsWieFolgtTooltip}
                        euroValue={get(twoYearData, 'cost.cost_value', 0)}
                        averageValue={get(twoYearData, 'cost.cost_base_value', 0)}
                        percentValue={get(twoYearData, 'cost.cost_percentage', 0)} />
                      <ExAnteAverageLine
                        title="Im dritten Jahr"
                        numbersTooltip={dieKostenReduzierenDieRenditeDesInvestmentsWieFolgtTooltip}
                        euroValue={get(threeYearData, 'cost.cost_value', 0)}
                        averageValue={get(threeYearData, 'cost.cost_base_value', 0)}
                        percentValue={get(threeYearData, 'cost.cost_percentage', 0)} />
                      <ExAnteAverageLine
                        title="Im vierten Jahr"
                        numbersTooltip={dieKostenReduzierenDieRenditeDesInvestmentsWieFolgtTooltip}
                        euroValue={get(fourYearData, 'cost.cost_value', 0)}
                        averageValue={get(fourYearData, 'cost.cost_base_value', 0)}
                        percentValue={get(fourYearData, 'cost.cost_percentage', 0)} />
                      <ExAnteAverageLine
                        title="Im fünften Jahr"
                        numbersTooltip={dieKostenReduzierenDieRenditeDesInvestmentsWieFolgtTooltip}
                        euroValue={get(fiverYearData, 'cost.cost_value', 0)}
                        averageValue={get(fiverYearData, 'cost.cost_base_value', 0)}
                        percentValue={get(fiverYearData, 'cost.cost_percentage', 0)} />
                      <ExAnteAverageLine
                        euroValue={null}
                        percentValue={null}
                        footer
                        title="Gesamtdurchschnitt"
                        numbersTooltip={dieKostenReduzierenDieRenditeDesInvestmentsWieFolgtTooltip}
                        averageValue={get(exAnteData, `${simulationYears}.ongoing.cost_base_value`, 0)}/>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      }
      customClasses={{
        headerContainer: classes.headerContainer,
        container: classes.widgetContainer,
        contentContainer: classes.contentContainer,
      }}
    />
  )
}

PortfolioExAnteV2.propTypes = {
  totalValue: PropTypes.number.isRequired,
  exAnteData: PropTypes.object.isRequired
};

export default PortfolioExAnteV2

const BlueChip = ({label}) => {
  const classes = useStyles();

  return (
    <Chip
      label={label}
      className={classes.chip}
      size={'small'}
    />
  );
};

const TextWithTooltip = ({title, children}) => {
  const classes = useStyles();

  return !!title ?
    <Tooltip arrow placement={"top"} classes={{tooltip: classes.tooltip}} title={title}>
      <span>{children}</span>
    </Tooltip>
   : children
};